import React, { useMemo, useState } from "react";
import { G, useStore, useIsGM, openWindow, controlsChar } from "../store";
import { create } from "../net";
import { blank5e } from "../../shared/sheet5e";
import type { Char, Handout } from "../../shared/types";
import { prompt } from "../util";

type Item = { kind: "char"; rec: Char } | { kind: "handout"; rec: Handout };

export function JournalPanel() {
  const gm = useIsGM();
  const chars = useStore(G, s => s.recs.char);
  const handouts = useStore(G, s => s.recs.handout);
  const settings = useStore(G, s => s.game?.settings);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const items: Item[] = useMemo(() => [
    ...Object.values(chars).map(rec => ({ kind: "char" as const, rec })),
    ...Object.values(handouts).map(rec => ({ kind: "handout" as const, rec })),
  ], [chars, handouts]);

  const filtered = items.filter(i => (showArchived ? i.rec.archived : !i.rec.archived) && (!q || i.rec.name.toLowerCase().includes(q.toLowerCase())));
  const tree = useMemo(() => {
    const groups = new Map<string, Item[]>();
    for (const i of filtered) {
      const f = (i.rec.folder || "").trim();
      if (!groups.has(f)) groups.set(f, []);
      groups.get(f)!.push(i);
    }
    for (const list of groups.values()) list.sort((a, b) => a.rec.name.localeCompare(b.rec.name));
    return [...groups.entries()].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
  }, [filtered]);

  const newChar = async (npc: boolean) => {
    const name = await prompt(npc ? "NPC name" : "Character name", npc ? "Goblin" : "");
    if (!name) return;
    const sheet = settings?.sheet || "dnd5e";
    const id = await create("char", { name, sheet, attrs: sheet === "dnd5e" ? blank5e(npc) : {}, rep: {}, abilities: [], folder: npc ? "NPCs" : "" });
    openWindow("char", id);
  };
  const newHandout = async () => {
    const name = await prompt("Handout name");
    if (!name) return;
    const id = await create("handout", { name });
    openWindow("handout", id);
  };

  return (
    <div className="journal">
      <div className="art-bar">
        {(gm || settings?.playersCreateChars) && <button className="primary" onClick={() => newChar(false)}>＋ Character</button>}
        {gm && <button onClick={() => newChar(true)}>＋ NPC</button>}
        {gm && <button onClick={newHandout}>＋ Handout</button>}
      </div>
      <div className="art-bar">
        <input placeholder="Search journal" value={q} onChange={e => setQ(e.target.value)} />
        {gm && <label className="chk small"><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> archived</label>}
      </div>
      <div className="journal-list">
        {tree.map(([folder, list]) => (
          <div key={folder} className="jfolder">
            {folder && (
              <div className="jfolder-head" onClick={() => setCollapsed({ ...collapsed, [folder]: !collapsed[folder] })}>
                {collapsed[folder] ? "▸" : "▾"} 📁 {folder} <span className="dim">({list.length})</span>
              </div>
            )}
            {!collapsed[folder] && list.map(i => (
              <div
                key={i.rec.id}
                className={`jitem${folder ? " indent" : ""}`}
                draggable={i.kind === "char"}
                onDragStart={e => i.kind === "char" && e.dataTransfer.setData("application/x-hearth-char", i.rec.id)}
                onClick={() => openWindow(i.kind, i.rec.id)}
                title={i.kind === "char" ? "Click to open · drag onto the map to place a token" : "Click to open"}
              >
                {i.kind === "char" ? (
                  (i.rec as Char).avatar ? <img src={(i.rec as Char).avatar} alt="" /> : <span className="jicon">👤</span>
                ) : (i.rec as Handout).image ? <img src={(i.rec as Handout).image} alt="" /> : <span className="jicon">📜</span>}
                <span className="grow">{i.rec.name}</span>
                {i.kind === "char" && (i.rec as Char).attrs?.npc === "1" && <span className="pill dim">NPC</span>}
                {gm && !(i.rec.inJournals || []).length && <span className="pill dim" title="Not visible to players">hidden</span>}
                {!gm && i.kind === "char" && controlsChar(i.rec as Char) && <span className="pill">yours</span>}
              </div>
            ))}
          </div>
        ))}
        {!filtered.length && <div className="dim pad">{q ? "Nothing matches." : gm ? "Create characters, NPCs and handouts. Drag a character onto the map to make a token." : "Nothing has been shared with you yet."}</div>}
      </div>
    </div>
  );
}
